import { useState } from 'react';
import { formatLabel, BADGE } from '@/lib/format';

/**
 * Resolution Modal — Mandatory mitigation comment for all alert/hit resolutions.
 * Used across: Match Alerts, Monitoring Hits, Media Alerts
 */

const QUICK_REASONS = {
  false_positive: [
    { label: 'DOB Mismatch', template: 'Date of birth does not match. Case subject DOB differs from listed person DOB.' },
    { label: 'Different Nationality', template: 'Different nationality. Case subject and listed person are from different countries.' },
    { label: 'Different Gender', template: 'Different gender. Case subject and listed person are different genders.' },
    { label: 'Partial Name Only', template: 'Name match is partial only. Full names are materially different when compared in context.' },
    { label: 'Confirmed Different', template: 'Confirmed different individual/entity based on identity verification documents on file.' },
    { label: 'Insufficient Data', template: 'Insufficient information on the listed record to establish a match. No shared identifiers.' },
  ],
  true_positive: [
    { label: 'Identity Confirmed', template: 'Identity confirmed through document verification. Match is a true positive.' },
    { label: 'Multiple Identifiers Match', template: 'Multiple identifiers match (name, DOB, nationality). Escalating for enhanced review.' },
  ],
  inconclusive: [
    { label: 'Need More Info', template: 'Insufficient information to determine match. Additional documentation required from client.' },
    { label: 'Partial Match', template: 'Partial identifiers match but unable to confirm or deny. Requires further investigation.' },
  ],
  whitelisted: [
    { label: 'Recurring FP', template: 'Confirmed different individual on previous screening. Whitelisting to suppress future alerts.' },
  ],
  cleared: [
    { label: 'No Concern', template: 'Reviewed match details. No compliance concern identified based on available information.' },
    { label: 'Previously Resolved', template: 'This match was previously reviewed and resolved. No new information changes the assessment.' },
  ],
  escalated: [
    { label: 'High Score Match', template: 'High-confidence match requiring senior review. Multiple identifiers align with listed person.' },
    { label: 'PEP Identified', template: 'Politically Exposed Person identified. Escalating for Enhanced Due Diligence assessment.' },
  ],
  dismissed: [
    { label: 'Low Relevance', template: 'Match score is low and no shared identifiers. Dismissed as not relevant.' },
    { label: 'No New Information', template: 'No new information since last review. Dismissing as previously assessed.' },
  ],
  flagged: [
    { label: 'Compliance Concern', template: 'Article raises potential compliance concern requiring further investigation.' },
  ],
};

const PROMPTS = {
  false_positive: 'Why is this a false positive? Provide basis for your determination.',
  true_positive: 'Confirm why this is a true positive match.',
  inconclusive: 'Why is this inconclusive? What additional information is needed?',
  whitelisted: 'Why should this match be whitelisted for future screenings?',
  cleared: 'Why is this hit cleared? Provide basis for no compliance concern.',
  escalated: 'Why is this being escalated? Describe the concern.',
  dismissed: 'Why is this being dismissed? Provide reason.',
  flagged: 'What specific concern does this article raise?',
};

export default function ResolutionModal({ isOpen, resolution, matchName, onConfirm, onCancel, caseData, matchData }) {
  const [comment, setComment] = useState('');
  if (!isOpen) return null;

  const reasons = QUICK_REASONS[resolution] || [];
  const prompt = PROMPTS[resolution] || 'Provide your reason for this resolution.';
  const isValid = comment.trim().length >= 10;

  const handleQuickReason = (template) => {
    let filled = template;
    if (caseData) {
      filled = filled.replace('{case_dob}', caseData.date_of_birth || 'N/A');
      filled = filled.replace('{case_nationality}', caseData.nationality || 'N/A');
    }
    if (matchData) {
      filled = filled.replace('{match_dob}', matchData.date_of_birth || 'N/A');
      filled = filled.replace('{match_nationality}', matchData.nationality || matchData.country || 'N/A');
    }
    setComment(filled);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={onCancel}>
      <div className="bg-white rounded-lg border border-[#E4E4E7] shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E4E4E7]">
          <h2 className="font-heading text-base font-semibold">Resolve Alert</h2>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-[#52525B]">Resolution:</span>
            <span className={`${BADGE} ${resolution === 'true_positive' || resolution === 'escalated' ? 'bg-red-100 text-red-700 border-red-300' : resolution === 'false_positive' || resolution === 'cleared' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-gray-100 text-gray-700 border-gray-300'}`}>{formatLabel(resolution)}</span>
            {matchName && <span className="text-xs text-[#A1A1AA]">— {matchName}</span>}
          </div>
        </div>

        <div className="px-5 py-4">
          <p className="text-sm text-[#52525B] mb-3">{prompt}</p>

          {/* Quick Reasons */}
          {reasons.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-[#A1A1AA] mb-1.5">Quick reasons:</p>
              <div className="flex flex-wrap gap-1.5">
                {reasons.map((r, i) => (
                  <button key={i} onClick={() => handleQuickReason(r.template)} className="px-2.5 py-1 text-xs border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1] transition-all text-[#52525B]">{r.label}</button>
                ))}
              </div>
            </div>
          )}

          {/* Comment Field */}
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4} placeholder="Enter your mitigation comment (minimum 10 characters)..." className="w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#0052FF] resize-none" autoFocus />
          <div className="flex justify-between mt-1">
            <span className={`text-xs ${comment.trim().length < 10 ? 'text-red-500' : 'text-green-600'}`}>{comment.trim().length}/10 min characters</span>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-[#E4E4E7] flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm border border-[#E4E4E7] rounded-md hover:bg-[#F0F0F1]">Cancel</button>
          <button onClick={() => { onConfirm(comment.trim()); setComment(''); }} disabled={!isValid} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-md hover:bg-[#0040CC] disabled:opacity-40 disabled:cursor-not-allowed">Confirm Resolution</button>
        </div>
      </div>
    </div>
  );
}
